import React, { useCallback, useEffect } from 'react';
import { MenuPosition } from '../../BreakdownMain/BreakdownMain.types';

/**
 * 浮动组件位置更新 Hook
 * 负责管理 SetSearch 和 LocationSearch 推荐组件的位置更新
 * 监听表格滚动、窗口滚动和组件可见性变化，自动更新浮动组件位置
 */
export const useFloatingComponentPosition = (
  setSearchVisible: boolean,
  activeSceneId: string | null,
  sceneLocationSearchVisible: boolean,
  activeLocationSceneId: string | null,
  setSetSearchPosition: React.Dispatch<React.SetStateAction<MenuPosition | null>>,
  setSceneLocationSearchPosition: React.Dispatch<React.SetStateAction<MenuPosition | null>>
) => {
  // 处理表格滚动时的组件位置更新
  const updateComponentPositions = useCallback(() => {
    // 更新推荐组件位置（场景）
    if (setSearchVisible && activeSceneId) {
      const inputElement = document.querySelector(
        `input[data-scene-id="${activeSceneId}"][data-field="sceneSet"]`
      ) as HTMLElement;
      const tableContainer = document.querySelector('.breakdown-table-container') as HTMLElement;
      
      if (inputElement && tableContainer) {
        const inputRect = inputElement.getBoundingClientRect();
        const containerRect = tableContainer.getBoundingClientRect();
        
        // 计算相对于表格容器的位置，宽度匹配输入框宽度
        setSetSearchPosition({
          top: inputRect.bottom - containerRect.top + 4,
          left: inputRect.left - containerRect.left,
          width: inputRect.width
        });
      }
    }
    
    // 更新推荐组件位置（地点）
    if (sceneLocationSearchVisible && activeLocationSceneId) {
      const inputElement = document.querySelector(
        `input[data-scene-id="${activeLocationSceneId}"][data-field="sceneLocation"]`
      ) as HTMLElement;
      const tableContainer = document.querySelector('.breakdown-table-container') as HTMLElement;
      
      if (inputElement && tableContainer) {
        const inputRect = inputElement.getBoundingClientRect();
        const containerRect = tableContainer.getBoundingClientRect();
        
        // 计算相对于表格容器的位置，宽度匹配输入框宽度，右对齐
        const componentWidth = inputRect.width; // 动态获取输入框宽度
        setSceneLocationSearchPosition({
          top: inputRect.bottom - containerRect.top + 4,
          left: inputRect.right - containerRect.left - componentWidth,
          width: componentWidth
        });
      }
    }
  }, [setSearchVisible, activeSceneId, sceneLocationSearchVisible, activeLocationSceneId, setSetSearchPosition, setSceneLocationSearchPosition]);

  // 监听表格滚动事件
  useEffect(() => {
    // 对于表格滚动，使用即时更新以获得最佳跟随效果
    const handleTableScroll = () => {
      updateComponentPositions();
    };
    
    // 对于窗口滚动和resize，使用轻微节流避免过度计算
    let windowScrollTimer: ReturnType<typeof setTimeout> | null = null;
    const handleWindowEvents = () => {
      if (windowScrollTimer) {
        clearTimeout(windowScrollTimer);
      }
      windowScrollTimer = setTimeout(() => {
        updateComponentPositions();
      }, 4); // 4ms延迟，几乎即时但避免过度调用
    };

    // 监听表格容器滚动 - 即时响应
    // 监听主容器和 Excel 表格容器的滚动事件
    const mainTableContainer = document.querySelector('.breakdown-table-container');
    const excelTableContainer = mainTableContainer?.querySelector('.excel-table-container') as HTMLElement;
    
    if (mainTableContainer) {
      mainTableContainer.addEventListener('scroll', handleTableScroll, { passive: true });
    }
    if (excelTableContainer) {
      excelTableContainer.addEventListener('scroll', handleTableScroll, { passive: true });
    }
    
    // 监听窗口滚动和resize - 轻微延迟
    window.addEventListener('scroll', handleWindowEvents, { passive: true });
    window.addEventListener('resize', handleWindowEvents, { passive: true });

    return () => {
      if (windowScrollTimer) {
        clearTimeout(windowScrollTimer);
      }
      if (mainTableContainer) {
        mainTableContainer.removeEventListener('scroll', handleTableScroll);
      }
      const currentExcelTableContainer = mainTableContainer?.querySelector('.excel-table-container') as HTMLElement;
      if (currentExcelTableContainer) {
        currentExcelTableContainer.removeEventListener('scroll', handleTableScroll);
      }
      window.removeEventListener('scroll', handleWindowEvents);
      window.removeEventListener('resize', handleWindowEvents);
    };
  }, [updateComponentPositions]);

  // 当组件可见性改变时，延迟更新位置（确保DOM已渲染）
  useEffect(() => {
    if (setSearchVisible || sceneLocationSearchVisible) {
      // 延迟一小段时间确保DOM完全渲染
      const timer = setTimeout(() => {
        updateComponentPositions();
      }, 10);
      
      return () => clearTimeout(timer);
    }
  }, [setSearchVisible, sceneLocationSearchVisible, updateComponentPositions]);
};

